import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { Activity, Loader2, CheckCircle } from 'lucide-react';
import { apiClient } from '@/lib/api-client';
import { ROUTES } from '@/lib/routes';

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      await apiClient.post('/v1/auth/forgot-password', { email });
      setSent(true);
    } catch {
      // Always show success to avoid email enumeration
      setSent(true);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-brand-dark via-[#1e3a2e] to-[#2a4a3a] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-brand bg-brand-primary mb-4 shadow-lg">
            <Activity size={26} className="text-white" />
          </div>
          <h1 className="font-heading text-2xl font-bold text-white">Dispatcher</h1>
          <p className="text-green-200/60 text-sm mt-1">Réinitialisation du mot de passe</p>
        </div>

        <div className="card p-8 shadow-xl">
          {sent ? (
            <div className="text-center space-y-4">
              <CheckCircle className="mx-auto text-brand-primary" size={40} />
              <h2 className="font-heading text-lg font-bold text-brand-dark">Lien envoyé</h2>
              <p className="text-sm text-gray-500">
                Si un compte est associé à cet email, vous recevrez un lien de réinitialisation dans
                les prochaines minutes.
              </p>
              <Link to={ROUTES.LOGIN} className="btn-primary inline-flex mt-2">
                Retour à la connexion
              </Link>
            </div>
          ) : (
            <>
              <h2 className="font-heading text-lg font-bold text-brand-dark mb-2">
                Mot de passe oublié
              </h2>
              <p className="text-sm text-gray-500 mb-6">
                Saisissez votre email pour recevoir un lien de réinitialisation.
              </p>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="label" htmlFor="email">Email</label>
                  <input
                    id="email"
                    type="email"
                    className="input"
                    placeholder="vous@exemple.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    autoFocus
                    autoComplete="email"
                  />
                </div>

                {error && (
                  <div role="alert" className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-brand px-3 py-2">
                    {error}
                  </div>
                )}

                <button type="submit" className="btn-primary w-full" disabled={loading}>
                  {loading && <Loader2 size={14} className="animate-spin" />}
                  Envoyer le lien
                </button>
              </form>

              <p className="text-center text-xs text-gray-400 mt-4">
                <Link to={ROUTES.LOGIN} className="hover:text-brand-primary transition-colors">
                  Retour à la connexion
                </Link>
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
